const BASE = '/api'

async function request(path, opts = {}) {
  const { method = 'GET', body, params, signal } = opts

  let url = `${BASE}${path}`
  if (params) {
    const qs = new URLSearchParams(params).toString()
    if (qs) url += `?${qs}`
  }

  const headers = {}
  const fetchOpts = { method, headers, credentials: 'include' }

  if (signal) fetchOpts.signal = signal

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    fetchOpts.body = JSON.stringify(body)
  }

  const res = await fetch(url, fetchOpts)

  if (!res.ok) {
    const text = await res.text()
    let message
    try { message = JSON.parse(text).error } catch { message = text }
    throw Object.assign(new Error(message || `HTTP ${res.status}`), { status: res.status })
  }

  if (res.status === 204) return null
  return res.json()
}

// --- Auth ---

export const auth = {
  signup: (data) => request('/auth/sign-up/email', { method: 'POST', body: data }),
  login: (data) => request('/auth/sign-in/email', { method: 'POST', body: data }),
  logout: () => request('/auth/sign-out', { method: 'POST', body: {} }),
  session: () => request('/auth/get-session'),
  forgotPassword: (email) => request('/auth/forget-password', { method: 'POST', body: { email, redirectTo: '/reset-password' } }),
  resetPassword: (token, newPassword) => request('/auth/reset-password', { method: 'POST', body: { token, newPassword } }),
}

// --- Projects ---

export const projects = {
  list: () => request('/projects'),
  get: (id) => request(`/projects/${id}`),
  create: (data) => request('/projects', { method: 'POST', body: data }),
  update: (id, data) => request(`/projects/${id}`, { method: 'PATCH', body: data }),
  delete: (id) => request(`/projects/${id}`, { method: 'DELETE' }),
}

// --- Members (scoped to project) ---

export const members = {
  invite: (projectId, data) => request(`/projects/${projectId}/members`, { method: 'POST', body: data }),
  updateRole: (projectId, userId, data) => request(`/projects/${projectId}/members/${userId}`, { method: 'PATCH', body: data }),
  remove: (projectId, userId) => request(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' }),
}

// --- Sessions (scoped to project) ---

export const sessions = {
  list: (projectId, params, opts) => request(`/projects/${projectId}/sessions`, { params, ...opts }),
  get: (projectId, sessionId, opts) => request(`/projects/${projectId}/sessions/${sessionId}`, { ...opts }),
  getDetail: (projectId, sessionId, { unmask, ...opts } = {}) => request(`/projects/${projectId}/sessions/${sessionId}/detail${unmask ? '?unmask=true' : ''}`, { ...opts }),
  delete: (projectId, sessionId) => request(`/projects/${projectId}/sessions/${sessionId}`, { method: 'DELETE' }),
}

// --- Tags (scoped to project) ---

export const tags = {
  list: (projectId, opts) => request(`/projects/${projectId}/tags`, { ...opts }),
  create: (projectId, data) => request(`/projects/${projectId}/tags`, { method: 'POST', body: data }),
  update: (projectId, tagId, data) => request(`/projects/${projectId}/tags/${tagId}`, { method: 'PATCH', body: data }),
  delete: (projectId, tagId) => request(`/projects/${projectId}/tags/${tagId}`, { method: 'DELETE' }),
  assign: (projectId, sessionId, tagId) => request(`/projects/${projectId}/sessions/${sessionId}/tags`, { method: 'POST', body: { tagId } }),
  remove: (projectId, sessionId, tagId) => request(`/projects/${projectId}/sessions/${sessionId}/tags/${tagId}`, { method: 'DELETE' }),
}

// --- Rubrics (scoped to project) ---

export const rubrics = {
  list: (projectId, opts) => request(`/projects/${projectId}/rubrics`, { ...opts }),
  get: (projectId, rubricId) => request(`/projects/${projectId}/rubrics/${rubricId}`),
  getVersion: (projectId, versionId) => request(`/projects/${projectId}/rubric-versions/${versionId}`),
  create: (projectId, data) => request(`/projects/${projectId}/rubrics`, { method: 'POST', body: data }),
  update: (projectId, rubricId, data) => request(`/projects/${projectId}/rubrics/${rubricId}`, { method: 'PUT', body: data }),
}

// --- Session Rubrics (scoped to project + session) ---

export const sessionRubrics = {
  list: (projectId, sessionId, opts) => request(`/projects/${projectId}/sessions/${sessionId}/rubrics`, { ...opts }),
  add: (projectId, sessionId, rubricId) => request(`/projects/${projectId}/sessions/${sessionId}/rubrics`, { method: 'POST', body: { rubricId } }),
  remove: (projectId, sessionId, rubricId) => request(`/projects/${projectId}/sessions/${sessionId}/rubrics/${rubricId}`, { method: 'DELETE' }),
}

// --- Evaluations (scoped to project + session) ---

export const evaluations = {
  list: (projectId, sessionId) => request(`/projects/${projectId}/sessions/${sessionId}/evaluations`),
  save: (projectId, sessionId, data) => request(`/projects/${projectId}/sessions/${sessionId}/evaluations`, { method: 'POST', body: data }),
  getSourceData: (projectId, sessionId) => request(`/projects/${projectId}/sessions/${sessionId}/evaluation-data`),
  getRecovery: (projectId, sessionId) => request(`/projects/${projectId}/sessions/${sessionId}/evaluation-recovery`),
}

// --- Highlight Types (scoped to project) ---

export const highlightTypes = {
  list: (projectId, opts) => request(`/projects/${projectId}/highlight-types`, { ...opts }),
  create: (projectId, data) => request(`/projects/${projectId}/highlight-types`, { method: 'POST', body: data }),
}

// --- Highlights (scoped to project + session) ---

export const highlights = {
  list: (projectId, sessionId, opts) => request(`/projects/${projectId}/sessions/${sessionId}/highlights`, { ...opts }),
  create: (projectId, sessionId, data) => request(`/projects/${projectId}/sessions/${sessionId}/highlights`, { method: 'POST', body: data }),
  delete: (projectId, highlightId) => request(`/projects/${projectId}/highlights/${highlightId}`, { method: 'DELETE' }),
}

// --- Comments (scoped to project + session) ---

export const comments = {
  list: (projectId, sessionId, opts) => request(`/projects/${projectId}/sessions/${sessionId}/comments`, { ...opts }),
  create: (projectId, sessionId, data) => request(`/projects/${projectId}/sessions/${sessionId}/comments`, { method: 'POST', body: data }),
  delete: (projectId, commentId) => request(`/projects/${projectId}/comments/${commentId}`, { method: 'DELETE' }),
}

// --- Uploads ---

export const uploads = {
  list: (projectId) => request(`/projects/${projectId}/uploads`),
  upload: (projectId, file) => {
    const form = new FormData()
    form.append('file', file)
    return fetch(`${BASE}/projects/${projectId}/upload`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        const text = await res.text()
        let message
        try { message = JSON.parse(text).error } catch { message = text }
        throw Object.assign(new Error(message || `HTTP ${res.status}`), { status: res.status })
      }
      return res.json()
    })
  },
  confirm: (projectId, data) =>
    request(`/projects/${projectId}/upload/confirm`, { method: 'POST', body: data }),
  delete: (projectId, uploadId) =>
    request(`/projects/${projectId}/uploads/${uploadId}`, { method: 'DELETE' }),
}

// --- Stats ---

export const stats = {
  get: (projectId) => request(`/projects/${projectId}/stats`),
}

// --- Eval Summary ---

export const evalSummary = {
  get: (projectId, params, opts) => request(`/projects/${projectId}/eval-summary`, { params, ...opts }),
}

// --- Tasks (scoped to project) ---

export const tasks = {
  list: (projectId) => request(`/projects/${projectId}/tasks`),
  get: (projectId, taskId) => request(`/projects/${projectId}/tasks/${taskId}`),
  create: (projectId, data) => request(`/projects/${projectId}/tasks`, { method: 'POST', body: data }),
  close: (projectId, taskId) => request(`/projects/${projectId}/tasks/${taskId}/close`, { method: 'POST' }),
  createInvite: (projectId, taskId, data) => request(`/projects/${projectId}/tasks/${taskId}/invites`, { method: 'POST', body: data }),
}

// --- Guest access (no auth, token-based) ---

export const guest = {
  getInvite: (projectId, token) => request(`/projects/${projectId}/invite/${token}`),
  acceptInvite: (projectId, token, data) => request(`/projects/${projectId}/invite/${token}/accept`, { method: 'POST', body: data }),
  listSessions: (projectId, token, params) => request(`/projects/${projectId}/guest/${token}/sessions`, { params }),
  getSession: (projectId, token, sessionId) => request(`/projects/${projectId}/guest/${token}/sessions/${sessionId}`),
}

// --- Embeddings & Similarity ---

export const embeddings = {
  getConfig: (projectId) => request(`/projects/${projectId}/embeddings/config`),
  saveConfig: (projectId, data) => request(`/projects/${projectId}/embeddings/config`, { method: 'POST', body: data }),
  getRoles: (projectId) => request(`/projects/${projectId}/embeddings/roles`),
  getStats: (projectId) => request(`/projects/${projectId}/embeddings/stats`),
  embedAll: (projectId) => request(`/projects/${projectId}/embeddings/embed-all`, { method: 'POST', body: {} }),
  embedSessions: (projectId, sessionIds) => request(`/projects/${projectId}/embeddings/embed`, { method: 'POST', body: { sessionIds } }),
  embedPairs: (projectId, sessionIds) => request(`/projects/${projectId}/embeddings/embed-pairs`, { method: 'POST', body: { sessionIds } }),
  clear: (projectId) => request(`/projects/${projectId}/embeddings/clear`, { method: 'POST', body: {} }),
}

export const similarity = {
  findSimilar: (projectId, sessionId, limit) => request(`/projects/${projectId}/sessions/${sessionId}/similar`, { params: { limit } }),
  findSimilarFiltered: (projectId, sessionId, params) => request(`/projects/${projectId}/sessions/${sessionId}/similar-filtered`, { params }),
  searchByText: (projectId, text, limit) => request(`/projects/${projectId}/similarity/search`, { method: 'POST', body: { text, limit } }),
  findSimilarPairs: (projectId, messageId, limit) => request(`/projects/${projectId}/messages/${messageId}/similar-pairs`, { params: { limit } }),
}

export const clusters = {
  run: (projectId, k) => request(`/projects/${projectId}/clusters/run`, { method: 'POST', body: { k } }),
  list: (projectId) => request(`/projects/${projectId}/clusters`),
  get: (projectId, clusterId) => request(`/projects/${projectId}/clusters/${clusterId}`),
  update: (projectId, clusterId, data) => request(`/projects/${projectId}/clusters/${clusterId}`, { method: 'PATCH', body: data }),
}

// --- Bulk Highlight Search ---

export const bulkHighlights = {
  search: (projectId, query, limit) => request(`/projects/${projectId}/highlights/search`, { method: 'POST', body: { query, limit } }),
  apply: (projectId, query, highlightTypeId) => request(`/projects/${projectId}/highlights/bulk`, { method: 'POST', body: { query, highlightTypeId } }),
  listBatches: (projectId) => request(`/projects/${projectId}/highlight-batches`),
  undoBatch: (projectId, batchId) => request(`/projects/${projectId}/highlight-batches/${batchId}`, { method: 'DELETE' }),
}

// --- PII Scan ---

export const piiScan = {
  run: (projectId) => request(`/projects/${projectId}/pii-scan/run`, { method: 'POST', body: {} }),
  scanSession: (projectId, sessionId) => request(`/projects/${projectId}/sessions/${sessionId}/pii-scan`, { method: 'POST', body: {} }),
  listBatches: (projectId) => request(`/projects/${projectId}/pii-scan/batches`),
  undo: (projectId, batchId) => request(`/projects/${projectId}/pii-scan/batches/${batchId}/undo`, { method: 'POST', body: {} }),
}

// --- Predictions ---

export const predictions = {
  getConfig: (projectId) => request(`/projects/${projectId}/predictions/config`),
  saveConfig: (projectId, data) => request(`/projects/${projectId}/predictions/config`, { method: 'POST', body: data }),
  predictSession: (projectId, sessionId, data) => request(`/projects/${projectId}/sessions/${sessionId}/predict-tags`, { method: 'POST', body: data }),
  run: (projectId, data) => request(`/projects/${projectId}/predictions/run`, { method: 'POST', body: data }),
  listJobs: (projectId) => request(`/projects/${projectId}/predictions/jobs`),
  getJob: (projectId, jobId) => request(`/projects/${projectId}/predictions/jobs/${jobId}`),
  cancel: (projectId, jobId) => request(`/projects/${projectId}/predictions/jobs/${jobId}/cancel`, { method: 'POST', body: {} }),
  undo: (projectId, jobId) => request(`/projects/${projectId}/predictions/jobs/${jobId}/undo`, { method: 'POST', body: {} }),
}

// --- Export ---

export const projectExport = {
  generate: (projectId, options) => request(`/projects/${projectId}/export`, { params: options }),
}

// --- Audit log ---

export const auditLog = {
  list: (projectId, params) => request(`/projects/${projectId}/audit-log`, { params }),
}

// --- Bookmarks (user-scoped) ---

export const bookmarksApi = {
  list: (projectId, params) => request(`/projects/${projectId}/bookmarks`, { params }),
  add: (projectId, sessionId, data) => request(`/projects/${projectId}/sessions/${sessionId}/bookmark`, { method: 'POST', body: data || {} }),
  remove: (projectId, sessionId) => request(`/projects/${projectId}/sessions/${sessionId}/bookmark`, { method: 'DELETE' }),
}

// --- Search History & Saved Searches (user-scoped) ---

export const searchHistoryApi = {
  list: (projectId, params) => request(`/projects/${projectId}/search-history`, { params }),
  record: (projectId, data) => request(`/projects/${projectId}/search-history`, { method: 'POST', body: data }),
  listSaved: (projectId) => request(`/projects/${projectId}/saved-searches`),
  save: (projectId, data) => request(`/projects/${projectId}/saved-searches`, { method: 'POST', body: data }),
  deleteSaved: (projectId, id) => request(`/projects/${projectId}/saved-searches/${id}`, { method: 'DELETE' }),
}

// --- Notifications (user-scoped) ---

export const notificationsApi = {
  list: (projectId, params) => request(`/projects/${projectId}/notifications`, { params }),
  unreadCount: (projectId) => request(`/projects/${projectId}/notifications/unread-count`),
  markRead: (projectId, ids) => request(`/projects/${projectId}/notifications/mark-read`, { method: 'POST', body: { ids } }),
  markAllRead: (projectId) => request(`/projects/${projectId}/notifications/mark-all-read`, { method: 'POST' }),
}

// --- My Activity (user-scoped) ---

export const myActivityApi = {
  list: (projectId, params) => request(`/projects/${projectId}/my-activity`, { params }),
}

// --- Scenario Packs ---

export const scenarioPacks = {
  list: (projectId) => request(`/projects/${projectId}/scenario-packs`),
  get: (projectId, packId) => request(`/projects/${projectId}/scenario-packs/${packId}`),
  create: (projectId, data) => request(`/projects/${projectId}/scenario-packs`, { method: 'POST', body: data }),
  update: (projectId, packId, data) => request(`/projects/${projectId}/scenario-packs/${packId}`, { method: 'PATCH', body: data }),
  delete: (projectId, packId) => request(`/projects/${projectId}/scenario-packs/${packId}`, { method: 'DELETE' }),
  // Scenarios
  createScenario: (projectId, packId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios`, { method: 'POST', body: data }),
  updateScenario: (projectId, packId, scenarioId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}`, { method: 'PATCH', body: data }),
  deleteScenario: (projectId, packId, scenarioId) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}`, { method: 'DELETE' }),
  reorderScenarios: (projectId, packId, ids) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/reorder`, { method: 'POST', body: { ids } }),
  // Details
  createDetail: (projectId, packId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/details`, { method: 'POST', body: data }),
  updateDetail: (projectId, packId, detailId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/details/${detailId}`, { method: 'PATCH', body: data }),
  deleteDetail: (projectId, packId, detailId) => request(`/projects/${projectId}/scenario-packs/${packId}/details/${detailId}`, { method: 'DELETE' }),
  createCondition: (projectId, packId, detailId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/details/${detailId}/conditions`, { method: 'POST', body: data }),
  deleteCondition: (projectId, packId, conditionId) => request(`/projects/${projectId}/scenario-packs/${packId}/conditions/${conditionId}`, { method: 'DELETE' }),
  // Scenario-Detail Relevance
  setScenarioDetails: (projectId, packId, scenarioId, detailIds) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/details`, { method: 'PUT', body: { detailIds } }),
  // Criteria
  getCriteria: (projectId, packId, scenarioId) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/criteria`),
  createCriterion: (projectId, packId, scenarioId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/criteria`, { method: 'POST', body: data }),
  updateCriterion: (projectId, packId, criterionId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/criteria/${criterionId}`, { method: 'PATCH', body: data }),
  deleteCriterion: (projectId, packId, criterionId) => request(`/projects/${projectId}/scenario-packs/${packId}/criteria/${criterionId}`, { method: 'DELETE' }),
  setCriterionScope: (projectId, packId, criterionId, scope) => request(`/projects/${projectId}/scenario-packs/${packId}/criteria/${criterionId}/scope`, { method: 'PUT', body: { scope } }),
  splitCriterion: (projectId, packId, criterionId, newScope) => request(`/projects/${projectId}/scenario-packs/${packId}/criteria/${criterionId}/split`, { method: 'POST', body: { newScope } }),
  reorderCriteria: (projectId, packId, scenarioId, ids) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/criteria/reorder`, { method: 'PUT', body: { ids } }),
  // Coverage
  getCoverage: (projectId, packId, scenarioId) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/coverage`),
  // Noise
  setOmissionFreq: (projectId, packId, scenarioId, detailId, omissionFreq) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/details/${detailId}/omission`, { method: 'PUT', body: { omissionFreq } }),
  getIrrelevantDetails: (projectId, packId, scenarioId) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/irrelevant-details`),
  setIrrelevantDetails: (projectId, packId, scenarioId, detailIds) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/irrelevant-details`, { method: 'PUT', body: { detailIds } }),
  setIrrelevantDetailFreq: (projectId, packId, scenarioId, detailId, inclusionFreq) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/irrelevant-details/${detailId}/frequency`, { method: 'PUT', body: { inclusionFreq } }),
  getNoiseSnippets: (projectId, packId, scenarioId) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/noise-snippets`),
  createNoiseSnippet: (projectId, packId, scenarioId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/noise-snippets`, { method: 'POST', body: data }),
  updateNoiseSnippet: (projectId, packId, snippetId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/noise-snippets/${snippetId}`, { method: 'PATCH', body: data }),
  deleteNoiseSnippet: (projectId, packId, snippetId) => request(`/projects/${projectId}/scenario-packs/${packId}/noise-snippets/${snippetId}`, { method: 'DELETE' }),
  // Generation
  getGenerationConfig: (projectId) => request(`/projects/${projectId}/generation-config`),
  setGenerationConfig: (projectId, data) => request(`/projects/${projectId}/generation-config`, { method: 'PUT', body: data }),
  generate: (projectId, packId, scenarioId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/generate`, { method: 'POST', body: data }),
  getGenerations: (projectId, packId, scenarioId) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/generations`),
  updateGeneration: (projectId, packId, generationId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/generations/${generationId}`, { method: 'PATCH', body: data }),
  forkGeneration: (projectId, packId, generationId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/generations/${generationId}/fork`, { method: 'POST', body: data }),
  deleteGeneration: (projectId, packId, generationId) => request(`/projects/${projectId}/scenario-packs/${packId}/generations/${generationId}`, { method: 'DELETE' }),
  generateMore: (projectId, packId, generationId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/generations/${generationId}/more`, { method: 'POST', body: data }),
  writeGeneration: (projectId, packId, scenarioId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/write`, { method: 'POST', body: data }),
  exportGenerations: (projectId, packId, scenarioId, data) => request(`/projects/${projectId}/scenario-packs/${packId}/scenarios/${scenarioId}/generations/export`, { method: 'POST', body: data }),
}

// --- Style Presets (project-level) ---

export const stylePresets = {
  list: (projectId) => request(`/projects/${projectId}/style-presets`),
  create: (projectId, data) => request(`/projects/${projectId}/style-presets`, { method: 'POST', body: data }),
  update: (projectId, presetId, data) => request(`/projects/${projectId}/style-presets/${presetId}`, { method: 'PATCH', body: data }),
  delete: (projectId, presetId) => request(`/projects/${projectId}/style-presets/${presetId}`, { method: 'DELETE' }),
  extract: (projectId, filter) => request(`/projects/${projectId}/style-presets/extract`, { method: 'POST', body: filter ? { filter } : {} }),
  importExamples: (projectId, presetId, sessionIds) =>
    request(`/projects/${projectId}/style-presets/${presetId}/import-examples`, { method: 'POST', body: { sessionIds } }),
}

// --- Scenario Pool ---

export const scenarioPool = {
  list: (projectId) => request(`/projects/${projectId}/scenario-pool`),
  add: (projectId, generationIds) => request(`/projects/${projectId}/scenario-pool`, { method: 'POST', body: { generationIds } }),
  update: (projectId, itemId, data) => request(`/projects/${projectId}/scenario-pool/${itemId}`, { method: 'PATCH', body: data }),
  remove: (projectId, ids) => request(`/projects/${projectId}/scenario-pool`, { method: 'DELETE', body: { ids } }),
}

// --- Project Invites (owner-managed invite links) ---

export const projectInvites = {
  list: (projectId) => request(`/projects/${projectId}/invites`),
  create: (projectId, data) => request(`/projects/${projectId}/invites`, { method: 'POST', body: data }),
  revoke: (projectId, inviteId) => request(`/projects/${projectId}/invites/${inviteId}/revoke`, { method: 'POST' }),
}

// --- Join (project invite acceptance) ---

export const join = {
  validate: (code) => request(`/join/${code}`),
  accept: (code) => request(`/join/${code}`, { method: 'POST' }),
}

// --- Admin (platform-level, separate auth) ---

export const adminAuth = {
  login: (data) => request('/admin/auth/login', { method: 'POST', body: data }),
  logout: () => request('/admin/auth/logout', { method: 'POST' }),
  session: () => request('/admin/auth/session'),
}

export const admin = {
  getUsers: () => request('/admin/users'),
  updateUser: (userId, data) => request(`/admin/users/${userId}`, { method: 'PATCH', body: data }),
  getProjects: () => request('/admin/projects'),
  transferProject: (projectId, newOwnerId) => request(`/admin/projects/${projectId}/transfer`, { method: 'POST', body: { newOwnerId } }),
  getInvites: () => request('/admin/invites'),
  createInvite: (data) => request('/admin/invites', { method: 'POST', body: data }),
  deleteInvite: (inviteId) => request(`/admin/invites/${inviteId}`, { method: 'DELETE' }),
  getSettings: () => request('/admin/settings'),
  updateSettings: (data) => request('/admin/settings', { method: 'PATCH', body: data }),
}
